import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import TopBar from "../Common/TopBar";
import PartnerService from "../../Services/PartnerService";
import AnalyticsService from "../../Services/AnalyticsService";
import { Container, Tab, Tabs } from "@mui/material";
import ErrorBoundary from "../Common/ErrorBoundary";
import FieldAgentOrders from "./FieldAgentOrders";
import AgentService from "../../Services/AgentService";
import _ from "lodash";

let ORDER_TYPE_FILTERS = [
  { label: "My Orders", identifier: "MY_ORDERS", selected: true },
  { label: "CST Orders", identifier: "CST_ORDERS", selected: false },
  { label: "Field Orders", identifier: "FIELD_ORDERS", selected: false },
  {
    label: "Self Checkout Orders",
    identifier: "PARTNER_SELF_CHECKOUT_ORDERS",
    selected: false,
  },
];

export default function FieldAgentCommercePage() {
  const [currentTab, setCurrentTab] = useState(
    sessionStorage.getItem("currentCommerceTab")
      ? sessionStorage.getItem("currentCommerceTab")
      : "pendingForApproval"
  );
  const [ordersList, setOrdersList] = useState(null);
  const [totalOrdersCount, setTotalOrdersCount] = useState(0);
  const [currentOrdersCount, setCurrentOrdersCount] = useState(0);
  const [loading, setLoading] = useState("not_started");
  const [searchOrdersKeyword, setSearchOrdersKeyword] = useState("");
  const [orderDateRangeFilter, setOrderDateRangeFilter] = useState(null);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const ORDERS_TO_SHOW_IN_A_SINGLE_SCROLL = 10;

  const setFilterValueToDefault = (filterIdentifier) => {
    sessionStorage.removeItem("partnerOrderTabClicked", true);
    const selectedFilter = _.find(
      ORDER_TYPE_FILTERS,
      (filter) => filter.identifier === filterIdentifier
    );
    selectedFilter.selected = false;
  };

  const isFilterSelected = (filterIdentifier) => {
    const selectedFilter = _.find(
      ORDER_TYPE_FILTERS,
      (filter) =>
        filter.identifier === filterIdentifier && filter.selected === true
    );
    return selectedFilter !== undefined ? selectedFilter?.selected : false;
  };

  useEffect(() => {
    // When the user navigates to the "Partner Orders" page, we set the "My Orders" filter to true and the rest to false
    if (sessionStorage.getItem("partnerOrderTabClicked")) {
      ORDER_TYPE_FILTERS = ORDER_TYPE_FILTERS.map((filter) => {
        if (filter.identifier === "MY_ORDERS") {
          return { ...filter, selected: true };
        }
        return { ...filter, selected: false };
      });
    }
  }, []);

  useEffect(() => {
    setLoading("started");
    // when user applies date filter OR changes the tab, we need to fetch order list from the start again (offset = 0)
    const agentOrdersCB = getAgentOrders({
      fetchFromStarting: true,
      myOrders: isFilterSelected("MY_ORDERS"),
      cstOrders: isFilterSelected("CST_ORDERS"),
      fieldOrders: isFilterSelected("FIELD_ORDERS"),
      partnerSelfCheckOutOrders: isFilterSelected(
        "PARTNER_SELF_CHECKOUT_ORDERS"
      ),
    });
    return () => agentOrdersCB();
  }, [currentTab, orderDateRangeFilter]);

  const handleLogout = () => {
    PartnerService.logoutFieldAgent();
    AnalyticsService.pushEvent("Logout As Field Agent Clicked");
    navigate("/");
    window.location.reload();
  };

  const getAgentOrders = ({
    fetchFromStarting,
    myOrders,
    cstOrders,
    fieldOrders,
    partnerSelfCheckOutOrders,
  }) => {
    const { axiosRequest, abortController } = AgentService.getFieldAgentOrders(
      fetchFromStarting ? 0 : currentOrdersCount,
      ORDERS_TO_SHOW_IN_A_SINGLE_SCROLL,
      searchOrdersKeyword,
      orderDateRangeFilter,
      currentTab,
      myOrders,
      cstOrders,
      fieldOrders,
      partnerSelfCheckOutOrders
    );
    axiosRequest
      .then((response) => {
        if (response?.data?.responseData?.fieldOrders) {
          setOrdersList([
            ...(fetchFromStarting ? [] : ordersList || []),
            ...response?.data?.responseData?.fieldOrders,
          ]);
          setTotalOrdersCount(response?.data?.responseData?.totalFieldOrders);
          setCurrentOrdersCount(
            (fetchFromStarting ? 0 : currentOrdersCount) +
              response?.data?.responseData?.fieldOrders?.length
          );
        }
        setLoading("done");
        setError(null);
      })
      .catch((error) => {
        if (error?.message !== "canceled") {
          if (error.data) {
            setError(error.data);
          } else {
            setError(error);
          }
          setLoading("done");
          console.error("Failed to Get Field Agent Orders: ", error);
        }
      });
    return () => abortController.abort();
  };

  const handleSearch = (keyword) => {
    if (keyword) {
      setSearchOrdersKeyword(keyword);
      setOrdersList([]);
      setTotalOrdersCount(0);
      setCurrentOrdersCount(0);
      setLoading("started");
      const { axiosRequest } = AgentService.getFieldAgentOrders(
        0,
        ORDERS_TO_SHOW_IN_A_SINGLE_SCROLL,
        keyword,
        orderDateRangeFilter,
        currentTab
      );
      axiosRequest
        .then((response) => {
          if (response?.data?.responseData?.fieldOrders) {
            setOrdersList(response?.data?.responseData?.fieldOrders);
            setTotalOrdersCount(response?.data?.responseData?.totalFieldOrders);
            setCurrentOrdersCount(
              response?.data?.responseData?.fieldOrders?.length
            );
          }
          setLoading("done");
        })
        .catch((error) => {
          setLoading("done");
          console.error("Failed to Get Field Agent Orders: ", error);
        });
    } else {
      clearSearch();
    }
  };

  const clearSearch = () => {
    setSearchOrdersKeyword("");
    setOrdersList([]);
    setTotalOrdersCount(0);
    setCurrentOrdersCount(0);
    setLoading("started");
    const { axiosRequest } = AgentService.getFieldAgentOrders(
      0,
      ORDERS_TO_SHOW_IN_A_SINGLE_SCROLL,
      null,
      orderDateRangeFilter,
      currentTab
    );
    axiosRequest
      .then((response) => {
        if (response?.data?.responseData?.fieldOrders) {
          setOrdersList(response?.data?.responseData?.fieldOrders);
          setTotalOrdersCount(response?.data?.responseData?.totalFieldOrders);
          setCurrentOrdersCount(
            response?.data?.responseData?.fieldOrders?.length
          );
        }
        setLoading("done");
      })
      .catch((error) => {
        setLoading("done");
        console.error("Failed to Get Field Agent Orders: ", error);
      });
  };

  const handleTabClickedEvent = (tabName, pageName) => {
    AnalyticsService.pushEvent(`Tab Clicked`, {
      tabName: pageName,
      page: "Field Agent Orders Page",
    });
    setCurrentTab(tabName);
    sessionStorage.setItem("currentCommerceTab", tabName);
  };

  const handleClearOrderDateRangeFilter = () => {
    setOrderDateRangeFilter(null);
    AnalyticsService.pushEvent(`Button Clicked`, {
      type: "Clear All Filters",
      page: "My Orders",
    });
  };

  return (
    <>
      <TopBar title="My Orders" backNavigation={true} />

      <ErrorBoundary message={``}>
        <Tabs
          value={currentTab}
          centered
          sx={{
            background: "#ffffff",
            margin: "16px",
            marginBottom: "16px",
            padding: "0px 24px",
            boxShadow: "0px 4px 15px rgba(0, 0, 0, 0.05)",
            borderRadius: "20px",
          }}
        >
          <Tab
            label="Pending Approval"
            value={"pendingForApproval"}
            sx={{
              width: "33.3%",
              borderBottom: `${
                currentTab === `pendingForApproval`
                  ? `1px solid #9D2123`
                  : `none`
              }`,
              color: `${currentTab === `pendingForApproval` ? `#9D2123` : `#333333`}`,
              opacity: `${currentTab === `pendingForApproval` ? `1` : `0.6`}`,
              textTransform: "capitalize",
              fontSize: "15px",
              padding: "20px 16px",
            }}
            onClick={() => {
              handleTabClickedEvent(
                "pendingForApproval",
                "Pending Approval Orders"
              );
            }}
          />
          <Tab
            label="Unfulfillable Orders"
            value={"onHold"}
            sx={{
              width: "33.3%",
              borderBottom: `${
                currentTab === `onHold` ? `1px solid #9D2123` : `none`
              }`,
              color: `${currentTab === `onHold` ? `#9D2123` : `#333333`}`,
              opacity: `${currentTab === `onHold` ? `1` : `0.6`}`,
              textTransform: "capitalize",
              fontSize: "15px",
              padding: "20px 16px",
            }}
            onClick={() => {
              handleTabClickedEvent("onHold", "Unfulfillable Orders");
            }}
          />
          <Tab
            label="Confirmed Orders"
            value={"confirmed"}
            sx={{
              width: "33.3%",
              borderBottom: `${
                currentTab === `confirmed` ? `1px solid #9D2123` : `none`
              }`,
              color: `${currentTab === `confirmed` ? `#9D2123` : `#333333`}`,
              opacity: `${currentTab === `confirmed` ? `1` : `0.6`}`,
              textTransform: "capitalize",
              fontSize: "15px",
              padding: "20px 16px",
            }}
            onClick={() => {
              handleTabClickedEvent("confirmed", "Confirmed Orders");
            }}
          />
        </Tabs>
      </ErrorBoundary>

      <FieldAgentOrders
        loading={loading}
        setLoading={setLoading}
        ordersList={ordersList}
        currentCount={currentOrdersCount}
        totalCount={totalOrdersCount}
        getAgentOrders={getAgentOrders}
        orderDateRangeFilter={orderDateRangeFilter}
        setOrderDateRangeFilter={setOrderDateRangeFilter}
        handleClearOrderDateRangeFilter={handleClearOrderDateRangeFilter}
        handleSearch={handleSearch}
        clearSearch={clearSearch}
        searchKeyword={searchOrdersKeyword}
        currentTab={currentTab}
        error={error}
        ORDER_TYPE_FILTERS={ORDER_TYPE_FILTERS}
        setFilterValueToDefault={setFilterValueToDefault}
        isFilterSelected={isFilterSelected}
      />
    </>
  );
}
